import { useEffect } from 'react';
import { create } from 'zustand';
import { supabase } from '../../../config/supabase';

function toAppUser(user) {
  if (!user) return null;
  return {
    id: user.id,
    email: user.email,
    role: user.app_metadata?.role || 'customer',
  };
}

const useAuthStore = create((set, get) => ({
  user: null,
  isLoading: true,
  initialized: false,

  initialize: async () => {
    if (get().initialized) return;
    set({ initialized: true });

    const { data, error } = await supabase.auth.getSession();
    if (error) console.error('Could not restore the authentication session.', error);
    set({ user: toAppUser(data?.session?.user), isLoading: false });

    supabase.auth.onAuthStateChange((_event, session) => {
      set({ user: toAppUser(session?.user), isLoading: false });
    });
  },

  signUp: async (email, password) => {
    const { data, error } = await supabase.auth.signUp({ email, password });
    if (error) throw error;
    const user = toAppUser(data.user);
    set({ user: data.session ? user : null });
    return { user, needsEmailConfirmation: !data.session };
  },

  signIn: async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    const user = toAppUser(data.user);
    set({ user });
    return { user };
  },

  signOut: async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    set({ user: null });
  },
}));

export function useAuth() {
  const auth = useAuthStore();

  useEffect(() => {
    auth.initialize();
  }, [auth.initialize]);

  return auth;
}
